/**
 * Вероятность схемной модели реестра (:8020) — представление в интерфейсе.
 *
 * ЗАЧЕМ ОТДЕЛЬНЫЙ МОДУЛЬ
 * До контракта V2 эта величина приходила под именем `ml_risk_score`, подменяя
 * собой исходный балл витрины. Под одним заголовком «ML» соседствовали
 * risk-ranker витрины (шкала 0–10, диапазон 4.7–10.0) и вероятность схемной
 * логрегрессии (медиана ~0.02). Отсюда бралось противоречие на одном экране:
 * сводка ГРУЗОВ показывала «Средн. ML 7.2», а её drill — 0.09 на тех же кейсах.
 *
 * Теперь бэкенд отдаёт обе величины раздельно, и правило интерфейса простое:
 * заголовок «ML» принадлежит только исходному баллу, а вероятность выводится
 * под собственной подписью и в собственном масштабе — процентами, а не числом
 * «как будто 0–10».
 *
 * ЧТО ЗДЕСЬ ДОКАЗАНО (замеры 2026-08-10, SCENARIOS_6_7_SCORE_LINEAGE_AUDIT.md)
 *  • реестр отдаёт `p` в [0,1] (контракт RawScoreCasesResponse: `p: number | null`);
 *  • прежнее отображаемое значение получалось как `Math.round(p * 1000) / 100`,
 *    то есть `p × 10`: кейс 4_715 при p = 0.009 показывался как 0.09;
 *  • исходный балл того же кейса в витрине — 9.9682.
 *
 * ЧЕГО ЗДЕСЬ НЕТ
 * Никакого приведения вероятности к шкале 0–10: сходство с прежним числом было
 * бы ложным — это разные модели с разными целями.
 */

import type { Formatter } from '@/lib/i18n'

/*
 * Подписи величины — КЛЮЧИ словаря, одни на все поверхности, чтобы ярлыки не
 * разъезжались. Значения (ru/kk) живут в module.i18n.ts под `analytics.schemeProb.*`.
 * Резолвит t() в компоненте: здесь React-контекста нет.
 *
 * Гарантия «две величины не смешиваются» (anomaly-score-contract.render.test.tsx)
 * держится на этих же константах: тест сверяет отрендеренный текст, полученный
 * из CATALOG по ключу, поэтому проверка распространяется и на казахский.
 */

/** Ключ подписи величины. */
export const SCHEME_PROBABILITY_LABEL_KEY = 'analytics.schemeProb.label'

/** Ключ короткой подписи для узкой колонки таблицы. */
export const SCHEME_PROBABILITY_SHORT_LABEL_KEY = 'analytics.schemeProb.shortLabel'

/** Ключ пояснения под tooltip: почему это не ML-балл. */
export const SCHEME_PROBABILITY_HINT_KEY = 'analytics.schemeProb.hint'


/**
 * Вероятность → процент в русской локали.
 *
 * 0.23 → «23 %», 0.09 → «9 %», 0.009 → «0,9 %». Разделитель перед знаком —
 * неразрывный пробел, его ставит Intl; вручную строку не собираем.
 *
 * null / undefined / нечисловое → «—». Значение вне [0,1] тоже даёт «—»:
 * это не вероятность, и показывать её как процент было бы враньём.
 */
export function formatSchemeProbability(p: number | null | undefined, f: Formatter): string {
  if (p == null || !Number.isFinite(p) || p < 0 || p > 1) return '—'
  // style:'percent' сохранён намеренно: Intl сам ставит неразрывный пробел
  // перед знаком. f.percent() добавляет '%' вплотную — вывод изменился бы.
  return f.number(p, { style: 'percent', maximumFractionDigits: 2 })
}
